import { Link } from "@tanstack/react-router";
import { track } from "@/lib/analytics";
import { NewsletterForm } from "@/components/site/NewsletterForm";

export interface ConversionTarget {
  to: string;
  label: string;
  /** Short line explaining what the visitor gets. */
  detail?: string;
}

/**
 * The standard end-of-article conversion block.
 *
 * One primary destination (listen), one or more secondary destinations
 * (read / study / shop), and newsletter capture. Every click is reported as a
 * consent-aware analytics event so article → ecosystem movement is measurable.
 */
export function ArticleConversion({
  articleSlug,
  primary,
  secondary = [],
  newsletterSource,
}: {
  articleSlug: string;
  primary?: ConversionTarget | null;
  secondary?: ConversionTarget[];
  newsletterSource: string;
}) {
  const report = (target: string, position: "primary" | "secondary") =>
    track("article_conversion_click", { article: articleSlug, target, position });

  return (
    <aside
      aria-labelledby={`continue-${articleSlug}`}
      className="mt-16 rounded-2xl border hairline bg-temple p-8"
    >
      <p id={`continue-${articleSlug}`} className="text-xs uppercase tracking-[0.3em] text-gold">
        Continue into the work
      </p>

      {primary && (
        <div className="mt-5">
          <Link
            to={primary.to}
            onClick={() => report(primary.to, "primary")}
            className="inline-flex rounded-full bg-gold-gradient px-6 py-3 text-sm font-semibold text-obsidian shadow-gold"
          >
            {primary.label}
          </Link>
          {primary.detail && (
            <p className="mt-3 text-sm text-muted-foreground">{primary.detail}</p>
          )}
        </div>
      )}

      {secondary.length > 0 && (
        <ul className="mt-6 grid gap-3 text-sm sm:grid-cols-2">
          {secondary.map((s) => (
            <li key={s.to}>
              <Link
                to={s.to}
                onClick={() => report(s.to, "secondary")}
                className="text-gold underline decoration-gold/40 underline-offset-4 hover:text-gold-luminous hover:decoration-gold"
              >
                {s.label}
              </Link>
              {s.detail && <span className="text-muted-foreground"> — {s.detail}</span>}
            </li>
          ))}
        </ul>
      )}

      <div className="mt-8 border-t hairline pt-6">
        <p className="font-serif text-xl">
          New writings, music, and activations — sent with intention.
        </p>
        <div className="mt-4">
          <NewsletterForm source={newsletterSource} />
        </div>
      </div>
    </aside>
  );
}
